import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithPopup 
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../../firebase/config';
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaGraduationCap, 
  FaGoogle, 
  FaCheckCircle,
  FaArrowRight,
  FaEye,
  FaEyeSlash,
  FaUserGraduate,
  FaChalkboardTeacher
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';

const Register = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    userType: 'student',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const cities = [
    'Yaoundé', 'Douala', 'Bamenda', 'Buea', 'Limbe',
    'Bafoussam', 'Garoua', 'Maroua', 'Ngaoundéré', 'Kumba',
    'Other'
  ];

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (!formData.phone.match(/^[0-9\-\+]{9,15}$/)) {
      setError('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const createUserProfile = async (userCredential, additionalData = {}) => {
    const user = userCredential.user;
    
    // Update profile with display name
    await updateProfile(user, {
      displayName: formData.fullName || user.displayName
    });

    // Create user profile in Firestore
    const userData = {
      uid: user.uid,
      email: user.email,
      fullName: formData.fullName || user.displayName,
      phone: formData.phone || '',
      location: formData.location || '',
      userType: formData.userType,
      photoURL: user.photoURL || '',
      emailVerified: user.emailVerified,
      createdAt: new Date(),
      profileCompleted: formData.userType === 'student',
      status: 'active',
      ...additionalData
    };

    await setDoc(doc(db, 'users', user.uid), userData);

    // If user is a tutor, create additional tutor profile
    if (formData.userType === 'tutor') {
      const tutorData = {
        uid: user.uid,
        subjects: [],
        qualifications: [],
        hourlyRate: 0,
        availability: [],
        rating: 0,
        totalSessions: 0,
        totalReviews: 0,
        status: 'pending',
        profileComplete: false,
        createdAt: new Date(),
        education: '',
        experience: '',
        bio: '',
        teachingStyle: ''
      };

      await setDoc(doc(db, 'tutors', user.uid), tutorData);
    }

    return userData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await createUserProfile(userCredential);
      
      setSuccess('Account created successfully!');
      
      // Redirect based on user type
      setTimeout(() => {
        if (formData.userType === 'tutor') {
          navigate('/tutor/profile-setup');
        } else {
          navigate('/dashboard');
        }
      }, 1500);
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      const additionalData = {
        fullName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified
      };
      
      await createUserProfile(result, additionalData);
      
      setSuccess('Account created with Google!');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Google sign up error:', error);
      setError('Failed to sign up with Google.');
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="auth-wrapper">
        <div className="auth-container">
          {/* Left Side - Welcome Message */}
          <div className="auth-side">
            <div className="auth-side-content">
              <div className="auth-logo">
                <div className="logo-circle">
                  <FaUser />
                </div>
                <h1>Join Our Community</h1>
                <p className="logo-description">
                  Connect with qualified tutors or start teaching today
                </p>
              </div>
              
              <div className="registration-benefits">
                <h2>Why Join Us?</h2>
                
                <div className="benefits-list">
                  <div className="benefit">
                    <div className="benefit-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="benefit-text">
                      <h4>Find the Perfect Match</h4>
                      <p>Connect with tutors who match your learning style</p>
                    </div>
                  </div>
                  
                  <div className="benefit">
                    <div className="benefit-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="benefit-text">
                      <h4>Flexible Scheduling</h4>
                      <p>Book sessions at times that work for you</p>
                    </div>
                  </div>
                  
                  <div className="benefit">
                    <div className="benefit-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="benefit-text">
                      <h4>Verified Tutors</h4>
                      <p>All tutors undergo a verification process</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Registration Form */}
          <div className="auth-main">
            <div className="auth-card">
              <div className="auth-header">
                <h2>Create Your Account</h2>
                <p>Join thousands of students and tutors in Cameroon</p>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="message error-message">
                  <FaCheckCircle className="message-icon" />
                  <div className="message-content">
                    <h4>Registration Error</h4>
                    <p>{error}</p>
                  </div>
                </div>
              )}
              
              {/* Success Message */}
              {success && (
                <div className="message success-message">
                  <FaCheckCircle className="message-icon" />
                  <div className="message-content">
                    <h4>Success!</h4>
                    <p>{success}</p>
                  </div>
                </div>
              )}
              
              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="auth-form">
                {/* Two-column layout for name and email */}
                <div className="form-row">
                  <div className="form-group">
                    <div className="input-group">
                      <FaUser className="input-icon" />
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                        placeholder="Full name"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <div className="input-group">
                      <FaEnvelope className="input-icon" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Email address"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Two-column layout for password and confirm password */}
                <div className="form-row">
                  <div className="form-group">
                    <div className="input-group">
                      <FaLock className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Password"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                      <button 
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <div className="input-group">
                      <FaLock className="input-icon" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                        placeholder="Confirm password"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                      <button 
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Two-column layout for phone and location */}
                <div className="form-row">
                  <div className="form-group">
                    <div className="input-group">
                      <FaPhone className="input-icon" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="Phone number"
                        required
                        disabled={loading}
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <div className="input-group">
                      <FaMapMarkerAlt className="input-icon" />
                      <select
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        required
                        disabled={loading}
                        className="form-select"
                      >
                        <option value="">Select your city</option>
                        {cities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* User Type Selection */}
                <div className="form-group">
                  <label className="form-label">
                    <FaGraduationCap className="label-icon" />
                    I want to join as:
                  </label>
                  <div className="user-type-grid">
                    <label className={`user-type-card ${formData.userType === 'student' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="userType"
                        value="student"
                        checked={formData.userType === 'student'}
                        onChange={(e) => setFormData({...formData, userType: e.target.value})}
                        disabled={loading}
                      />
                      <div className="user-type-content">
                        <FaUserGraduate className="user-type-icon" />
                        <span className="user-type-title">Student</span>
                        <span className="user-type-description">Looking for tutors</span>
                      </div>
                    </label>
                    
                    <label className={`user-type-card ${formData.userType === 'tutor' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="userType"
                        value="tutor"
                        checked={formData.userType === 'tutor'}
                        onChange={(e) => setFormData({...formData, userType: e.target.value})}
                        disabled={loading}
                      />
                      <div className="user-type-content">
                        <FaChalkboardTeacher className="user-type-icon" />
                        <span className="user-type-title">Tutor</span>
                        <span className="user-type-description">Offer tutoring services</span>
                      </div>
                    </label>
                  </div>
                </div>
                
                {/* Terms Agreement */}
                <div className="terms-agreement">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      required 
                      disabled={loading}
                      className="checkbox-input"
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">
                      I agree to the <Link to="/terms" className="terms-link">Terms</Link> and <Link to="/privacy" className="terms-link">Privacy Policy</Link>
                    </span>
                  </label>
                </div>
                
                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="btn btn-primary btn-full"
                  disabled={loading}
                >
                  <span className="btn-content">
                    {loading ? 'Creating Account...' : 'Create Account'}
                    {!loading && <FaArrowRight className="btn-icon" />}
                  </span>
                  {loading && <span className="btn-loader"></span>}
                </button>
              </form>
              
              {/* Divider */}
              <div className="divider">
                <span className="divider-text">Or sign up with</span>
              </div>
              
              {/* Google Sign Up */}
              <button 
                className="btn btn-outline btn-full google-btn"
                onClick={handleGoogleSignUp}
                disabled={loading}
              >
                <FaGoogle className="google-icon" />
                Sign up with Google
              </button>
              
              {/* Sign In Link */}
              <div className="auth-footer">
                <p className="auth-footer-text">
                  Already have an account?{' '}
                  <Link to="/login" className="auth-link">
                    Sign in here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;